import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { isAuthenticated, ensureAccessToken } from '../auth/auth';
import { Spinner } from './common';

// Guards authenticated routes. Beyond checking that tokens exist, it VALIDATES the
// session on mount (refreshing an expired access token if possible). An
// unrecoverable session redirects straight to sign-in instead of rendering the app
// shell and leaving the user on a hanging "Loading." spinner while API calls 401.
export function RequireAuth({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [state, setState] = useState<'checking' | 'ok' | 'expired'>(() =>
    isAuthenticated() ? 'checking' : 'expired',
  );

  useEffect(() => {
    if (state !== 'checking') return;
    let cancelled = false;
    ensureAccessToken()
      .then((token) => {
        if (!cancelled) setState(token ? 'ok' : 'expired');
      })
      .catch(() => {
        if (!cancelled) setState('expired');
      });
    return () => {
      cancelled = true;
    };
  }, [state]);

  if (state === 'expired') {
    const returnTo = location.pathname + location.search;
    return <Navigate to="/signin" replace state={{ returnTo }} />;
  }
  if (state === 'checking') {
    return <Spinner label="Signing you in." />;
  }
  return <>{children}</>;
}
